import React from 'react';
import { cn } from '../../utils/cn';
import {
  PEERSONA_DIVIDER,
  PEERSONA_TEXT_MUTED,
  PEERSONA_TEXT_PRIMARY,
  PEERSONA_TEXT_SECONDARY,
} from '../peersona/peersona-styles';

/**
 * Single row primitive used by both assistant list views. Caller wires
 * the avatar, labels, and click handler; the row itself is unaware of
 * whether it represents Fi, a Peersona, or a future entry type.
 */
interface ConversationRowProps {
  avatar: React.ReactNode;
  title: string;
  /** Optional glyph inline to the left of the title. */
  titleLeading?: React.ReactNode;
  /** Preview text, clamped to 2 lines. */
  body?: string | null;
  /** Optional 3rd line under ``body`` — typically a relative time. */
  footerMeta?: React.ReactNode;
  ariaLabel: string;
  onClick: () => void;
}

export const ConversationRow: React.FC<ConversationRowProps> = ({
  avatar,
  title,
  titleLeading,
  body,
  footerMeta,
  ariaLabel,
  onClick,
}) => {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      style={{ borderTop: `1px solid ${PEERSONA_DIVIDER}`, borderBottom: `1px solid ${PEERSONA_DIVIDER}` }}
      className={cn(
        '-mt-px flex w-full items-center gap-4 px-4 py-4 text-left',
        'transition-colors hover:bg-gray-50 focus:outline-none focus-visible:bg-gray-50',
        'focus-visible:ring-2 focus-visible:ring-purple-300',
      )}
    >
      <div className="shrink-0">{avatar}</div>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span
          className="font-albert-sans inline-flex items-center gap-1 text-sm font-semibold leading-tight"
          style={{ color: PEERSONA_TEXT_PRIMARY }}
        >
          {titleLeading}
          <span className="truncate">{title}</span>
        </span>
        {body ? (
          <p
            className="font-open-sans line-clamp-2 break-words text-sm leading-snug"
            style={{ color: PEERSONA_TEXT_SECONDARY }}
          >
            {body}
          </p>
        ) : null}
        {footerMeta ? (
          <span className="font-albert-sans text-xs font-medium leading-tight" style={{ color: PEERSONA_TEXT_MUTED }}>
            {footerMeta}
          </span>
        ) : null}
      </div>
    </button>
  );
};
